import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';

/*
  ALS-77: "Effective date & time is one field" (2934:5932). The frames draw one field,
  "05 Jul 2026 · 09:00", where an earlier build had a date box and a separate hh:mm box; this is
  the pure half of the fix - parsing and formatting the single value DateTimeField shows and
  LookbookDraft.effective carries, apart from the popover mechanics.
*/

export const DISPLAY_FORMAT = 'dd-MMM-yyyy';
const SEPARATOR = ' · ';
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidTime(time: string): boolean {
    return TIME_PATTERN.test(time);
}

/** `05-Jul-2026 · 09:00`, or `''` when the day is not set yet - a time alone is not a value. */
export function formatDateTime(day: Date | undefined, time: string): string {
    if (!day || !isValid(day) || !isValidTime(time)) return '';
    return `${format(day, DISPLAY_FORMAT)}${SEPARATOR}${time}`;
}

/** The inverse: a blank or malformed value parses to no day and the fallback time, so the popover always has something sane to show. */
export function parseDateTime(
    value: string,
    fallbackTime = '12:00',
): { day: Date | undefined; time: string } {
    const [datePart, timePart] = value.split(SEPARATOR);
    const day = datePart ? parse(datePart, DISPLAY_FORMAT, new Date()) : undefined;
    const time = timePart && isValidTime(timePart) ? timePart : fallbackTime;
    return { day: day && isValid(day) ? day : undefined, time };
}

/*
  The wire form is London wall-clock time, never an instant. The legacy app decides when a
  lookbook goes live by comparing EffectiveDate with GETDATE() on a database that runs in London
  time, and every timestamp this project writes goes through DatabaseClock (Europe/London) for the
  same reason (ADR 0048). So "09:00" on the form is 09:00 in the column, summer or winter, and
  whatever zone the browser happens to be in plays no part: the value is built from and read
  back into its own parts, never through a Date's local or UTC fields.
*/

const WIRE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/i;

const LONDON = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/**
 * The wire form of a DateTimeField value: `yyyy-MM-ddTHH:mm:00`, the London wall-clock time as
 * typed, with no zone. Deliberately stricter than `parseDateTime`: that one fills in a fallback
 * time so the popover always has something to show when it reopens, which is exactly wrong here -
 * a day with no time must not silently become noon on the wire. Null for anything short of a
 * complete, valid value.
 */
export function toIsoDateTime(value: string): string | null {
    const [datePart, timePart] = value.split(SEPARATOR);
    if (!datePart || !timePart || !isValidTime(timePart)) return null;
    const day = parse(datePart, DISPLAY_FORMAT, new Date());
    if (!isValid(day)) return null;
    return `${format(day, 'yyyy-MM-dd')}T${timePart}:00`;
}

/**
 * The inverse of `toIsoDateTime`, for a record loaded back from the API: the stored London
 * wall-clock time becomes this field's own display value, exactly as it was typed. A value that
 * carries a zone (an instant from somewhere else) is read as the London time it falls on. `''`
 * for anything unparseable, so a bad value shows the field's placeholder rather than a raw
 * timestamp string.
 */
export function fromIsoDateTime(iso: string): string {
    const wall = ZONED.test(iso.trim()) ? londonWallClock(iso) : iso;
    const m = wall ? WIRE.exec(wall) : null;
    if (!m) return '';
    const [, year, month, date, hours, minutes] = m;
    const day = new Date(Number(year), Number(month) - 1, Number(date));
    if (!isValid(day) || day.getDate() !== Number(date)) return '';
    return formatDateTime(day, `${hours}:${minutes}`);
}

/** An instant as the London wall-clock time it falls on, in the wire form; null when it is not one. */
function londonWallClock(iso: string): string | null {
    const instant = new Date(iso);
    if (!isValid(instant)) return null;
    const part = (type: Intl.DateTimeFormatPartTypes) =>
        LONDON.formatToParts(instant).find((p) => p.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:00`;
}
